import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Topbar from "@/components/Topbar";
import { formatDate, formatDateTime } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Plus, Trash2, Cog, Loader2, Save, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const PROCESSING_TYPES = ["Sorting", "Baling", "Shredding", "Pelletizing", "Washing"];
const FORMS = ["loose", "baled", "flakes", "pellets"];
const MATERIALS = ["PET", "HDPE", "HDPE(N)", "PP", "PP(N)", "PE(N)", "PE(M)", "UBC", "Mixed Plastics"];

export default function ProcessingForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resumeId = searchParams.get("resume");
  const [received, setReceived] = useState([]);
  const [selected, setSelected] = useState({});
  const [processingTypes, setProcessingTypes] = useState([]);
  const [outputs, setOutputs] = useState([
    { _uid: `out-${Date.now()}`, material_type: "PET", form: "baled", weight_kg: "" },
  ]);
  const [busy, setBusy] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftList, setDraftList] = useState([]);       // all drafts owned by this user
  const [activeDraft, setActiveDraft] = useState(null); // { id, label, updated_at } — null = not-yet-persisted

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/collections", { params: { status: "Received" } });
        if (cancelled) return;
        setReceived(r.data.filter((c) => !c.used_in_processing));
      } catch (err) {
        console.debug("received-collections load failed", err);
      }
      try {
        const { data } = await api.get("/processing/drafts");
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setDraftList(list);
        // If the user landed here via a "Resume & edit" link (?resume=<id>),
        // auto-load that specific draft into the form.
        if (resumeId) {
          const match = list.find((x) => x.id === resumeId);
          if (match) resumeDraft(match);
        }
      } catch (err) {
        console.debug("drafts load failed", err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  function resumeDraft(d) {
    const restored = {};
    for (const id of d.input_collection_ids || []) restored[id] = true;
    setSelected(restored);
    setProcessingTypes(Array.isArray(d.processing_types) ? d.processing_types : []);
    setOutputs(
      Array.isArray(d.outputs) && d.outputs.length
        ? d.outputs.map((o, i) => ({
            _uid: `draft-${i}-${Math.random().toString(36).slice(2, 7)}`,
            material_type: o.material_type || "PET",
            form: o.form || "baled",
            weight_kg: o.weight_kg == null ? "" : String(o.weight_kg),
          }))
        : [{ _uid: `out-${Date.now()}`, material_type: "PET", form: "baled", weight_kg: "" }]
    );
    setActiveDraft({ id: d.id, label: d.label, updated_at: d.updated_at });
    toast.success(`Resumed draft: ${d.label || "Untitled"}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function startFresh() {
    setSelected({});
    setProcessingTypes([]);
    setOutputs([{ _uid: `out-${Date.now()}`, material_type: "PET", form: "baled", weight_kg: "" }]);
    setActiveDraft(null);
  }

  async function deleteDraftFromList(draftId) {
    if (!window.confirm("Discard this saved draft?")) return;
    try {
      await api.delete(`/processing/drafts/${draftId}`);
      setDraftList((prev) => prev.filter((d) => d.id !== draftId));
      if (activeDraft?.id === draftId) setActiveDraft(null);
      toast.success("Draft discarded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }

  const totalInput = useMemo(
    () =>
      received
        .filter((c) => selected[c.id])
        .reduce((s, c) => s + (parseFloat(c.received_weight_kg) || 0), 0),
    [selected, received]
  );

  // Per-collection material breakdown. Newer records use `materials: [{material_type, weight_kg}, ...]`;
  // older ones only have the top-level `material_type` + `received_weight_kg`. Normalise to a single
  // shape so the picker and the running-total summary can use the same data.
  function collectionMaterials(c) {
    const arr = Array.isArray(c.materials) ? c.materials : [];
    const cleaned = arr
      .map((m) => ({
        material_type: m.material_type,
        weight_kg: parseFloat(m.weight_kg) || 0,
      }))
      .filter((m) => m.material_type && m.weight_kg > 0);
    if (cleaned.length > 0) return cleaned;
    const w = parseFloat(c.received_weight_kg) || 0;
    return c.material_type && w > 0 ? [{ material_type: c.material_type, weight_kg: w }] : [];
  }

  // Running total by material across all currently-selected collections.
  // Ordered so it renders deterministically in the summary bar.
  const selectedByMaterial = useMemo(() => {
    const totals = {};
    for (const c of received) {
      if (!selected[c.id]) continue;
      for (const m of collectionMaterials(c)) {
        totals[m.material_type] = (totals[m.material_type] || 0) + m.weight_kg;
      }
    }
    return Object.entries(totals)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([material_type, weight_kg]) => ({ material_type, weight_kg }));
  }, [selected, received]);

  const totalOutput = useMemo(
    () => outputs.reduce((s, o) => s + (parseFloat(o.weight_kg) || 0), 0),
    [outputs]
  );

  const residual = Math.max(0, totalInput - totalOutput);
  const overflow = totalOutput > totalInput + 0.001;

  // Per-material mass balance — outputs of each material may not exceed the
  // kg of that same material available across selected collections. Mirrors
  // the server-side check in create_processing.
  const inputByMaterial = useMemo(() => {
    const totals = {};
    for (const { material_type, weight_kg } of selectedByMaterial) {
      totals[material_type] = (totals[material_type] || 0) + weight_kg;
    }
    return totals;
  }, [selectedByMaterial]);
  const outputByMaterial = useMemo(() => {
    const totals = {};
    for (const o of outputs) {
      const kg = parseFloat(o.weight_kg) || 0;
      if (!o.material_type || kg <= 0) continue;
      totals[o.material_type] = (totals[o.material_type] || 0) + kg;
    }
    return totals;
  }, [outputs]);
  // Per-material mass balance is CONDITIONAL. When the input batch contains
  // Mixed Plastics the operator is sorting a mixed stream into pure outputs
  // (PET/HDPE/…), so those pure outputs can legitimately exceed their own
  // input kg. The overall total-vs-total check still prevents mass creation.
  const hasMixedPlasticsInput = (inputByMaterial["Mixed Plastics"] || 0) > 0;
  const materialOverflow = useMemo(() => {
    if (hasMixedPlasticsInput) return [];
    const bad = [];
    for (const [mt, outKg] of Object.entries(outputByMaterial)) {
      const inKg = inputByMaterial[mt] || 0;
      if (outKg - inKg > 0.001) bad.push({ material_type: mt, in_kg: inKg, out_kg: outKg });
    }
    return bad;
  }, [outputByMaterial, inputByMaterial, hasMixedPlasticsInput]);

  function toggleType(t) {
    setProcessingTypes((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]
    );
  }

  function setOutput(idx, key, val) {
    setOutputs((prev) => prev.map((o, i) => (i === idx ? { ...o, [key]: val } : o)));
  }

  function addOutput() {
    setOutputs((prev) => [
      ...prev,
      { _uid: `out-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, material_type: "PET", form: "baled", weight_kg: "" },
    ]);
  }

  function removeOutput(idx) {
    setOutputs((prev) => prev.filter((_, i) => i !== idx));
  }

  async function submit() {
    if (busy) return;
    setBusy(true);
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (ids.length === 0) { setBusy(false); return toast.error("Select at least one collection"); }
    if (processingTypes.length === 0) { setBusy(false); return toast.error("Select at least one processing type"); }
    if (overflow) {
      setBusy(false);
      return toast.error(
        `Total output (${totalOutput.toFixed(2)}kg) cannot exceed total input (${totalInput.toFixed(2)}kg).`
      );
    }
    if (materialOverflow.length > 0) {
      const first = materialOverflow[0];
      setBusy(false);
      return toast.error(
        `${first.material_type}: outputs ${first.out_kg.toFixed(2)} kg exceed input ${first.in_kg.toFixed(2)} kg`,
      );
    }
    for (const o of outputs) {
      if (!o.weight_kg || parseFloat(o.weight_kg) <= 0) {
        setBusy(false);
        return toast.error("Each output needs a positive weight");
      }
    }
    try {
      await api.post("/processing", {
        input_collection_ids: ids,
        processing_types: processingTypes,
        outputs: outputs.map((o) => ({ ...o, weight_kg: parseFloat(o.weight_kg) })),
        submitted_draft_id: activeDraft?.id || null,
      });
      toast.success("Processing submitted");
      setActiveDraft(null);
      navigate("/facility");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  // Save the form as a draft. Skips validation intentionally so partial state
  // is fine. If an activeDraft is already loaded, we PATCH it; otherwise we
  // POST a new one and remember the returned id.
  async function saveDraft() {
    setSavingDraft(true);
    try {
      const payload = {
        input_collection_ids: Object.keys(selected).filter((k) => selected[k]),
        processing_types: processingTypes,
        outputs: outputs.map((o) => ({
          material_type: o.material_type,
          form: o.form,
          weight_kg: o.weight_kg === "" || o.weight_kg == null ? null : parseFloat(o.weight_kg),
        })),
      };
      let saved;
      if (activeDraft?.id) {
        const { data } = await api.patch(`/processing/drafts/${activeDraft.id}`, payload);
        saved = data;
      } else {
        const { data } = await api.post("/processing/drafts", payload);
        saved = data;
      }
      setActiveDraft({ id: saved.id, label: saved.label, updated_at: saved.updated_at });
      setDraftList((prev) => {
        const others = prev.filter((d) => d.id !== saved.id);
        return [saved, ...others];
      });
      toast.success(activeDraft?.id ? "Draft updated" : "Draft saved — you can resume it later.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSavingDraft(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Topbar subtitle="Processing" />
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 space-y-6" data-testid="processing-form-page">
        <button
          onClick={() => navigate("/facility")}
          data-testid="back-to-facility-btn"
          className="flex items-center gap-1.5 text-sm text-[#3F3F3F] hover:text-[#056839]"
        >
          <ArrowLeft className="w-4 h-4" /> Back to facility
        </button>

        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#69A488]">New entry</div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-[#000000] mt-1">
            Log a processing run
          </h1>
          <p className="text-sm text-[#3F3F3F] mt-1">
            Outputs are intermediate batches — destinations are logged later via shipments.
          </p>
        </div>

        {(draftList.length > 0 || activeDraft) && (
          <Card
            className="border-[#E4D5B7] bg-[#FBF6EB] rounded-xl"
            data-testid="drafts-chooser"
          >
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Save className="w-4 h-4 text-[#A0522D]" />
                  <div className="text-sm font-semibold text-[#000000]">
                    Saved drafts ({draftList.length})
                  </div>
                </div>
                {activeDraft && (
                  <button
                    type="button"
                    onClick={startFresh}
                    data-testid="start-fresh-btn"
                    className="text-xs inline-flex items-center gap-1 text-[#056839] hover:underline"
                  >
                    <Plus className="w-3 h-3" /> Start a fresh entry
                  </button>
                )}
              </div>
              {draftList.length === 0 ? (
                <div className="text-xs text-[#3F3F3F]/70 italic">No saved drafts yet.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {draftList.map((d) => {
                    const isActive = activeDraft?.id === d.id;
                    return (
                      <div
                        key={d.id}
                        data-testid={`draft-card-${d.id}`}
                        className={`flex items-start gap-3 p-3 rounded-lg border-2 transition-all ${
                          isActive
                            ? "border-[#056839] bg-white shadow-sm"
                            : "border-[#E4D5B7] bg-white hover:border-[#69A488]"
                        }`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-[#000000] truncate">
                            {d.label || "Untitled draft"}
                            {isActive && (
                              <span className="ml-2 text-[10px] uppercase tracking-wider text-[#056839] font-semibold">
                                Editing
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-[#3F3F3F]/70 mt-0.5">
                            Saved {formatDateTime(d.updated_at)}
                            {d.outputs?.length ? ` · ${d.outputs.length} output batch${d.outputs.length !== 1 ? "es" : ""}` : ""}
                          </div>
                        </div>
                        {!isActive && (
                          <button
                            type="button"
                            onClick={() => resumeDraft(d)}
                            data-testid={`resume-draft-${d.id}`}
                            className="text-xs px-2.5 py-1 rounded-md border border-[#056839] text-[#056839] hover:bg-[#056839] hover:text-white transition-colors"
                          >
                            Resume
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => deleteDraftFromList(d.id)}
                          data-testid={`delete-draft-${d.id}`}
                          className="p-1 rounded-md text-[#A0522D] hover:bg-[#A0522D]/10"
                          title="Discard this draft"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 1: select inputs */}
        <Card className="border-[#D2E4D6]/80 rounded-xl">
          <CardContent className="p-5 md:p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-semibold">1 · Select received collections</h2>
              <div className="text-sm text-[#3F3F3F]">
                Total input:{" "}
                <span className="font-mono font-semibold text-[#056839]" data-testid="total-input">
                  {totalInput.toFixed(2)} kg
                </span>
              </div>
            </div>
            {received.length === 0 ? (
              <div className="text-sm text-[#3F3F3F]/60 italic">
                No received collections available.
              </div>
            ) : (
              <>
                {(() => {
                  const allSelected = received.every((c) => selected[c.id]);
                  return (
                    <div className="flex items-center justify-between text-xs">
                      <button
                        type="button"
                        onClick={() => {
                          if (allSelected) {
                            setSelected({});
                          } else {
                            const next = {};
                            for (const c of received) next[c.id] = true;
                            setSelected(next);
                          }
                        }}
                        data-testid="select-all-inputs"
                        className="text-[#056839] font-medium hover:underline"
                      >
                        {allSelected ? "Unselect all" : `Select all (${received.length})`}
                      </button>
                      <span className="text-[#3F3F3F]/60">
                        {Object.values(selected).filter(Boolean).length} of {received.length} selected
                      </span>
                    </div>
                  );
                })()}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="inputs-list">
                {received.map((c) => (
                  <label
                    key={c.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      selected[c.id]
                        ? "border-[#056839] bg-[#D2E4D6]/40"
                        : "border-[#E4D5B7] bg-white hover:border-[#69A488]"
                    }`}
                  >
                    <Checkbox
                      checked={!!selected[c.id]}
                      onCheckedChange={(v) =>
                        setSelected((p) => ({ ...p, [c.id]: !!v }))
                      }
                      data-testid={`input-checkbox-${c.id}`}
                      className="mt-0.5"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm text-[#000000]">
                        {c.material_type}{" "}
                        <span className="font-mono text-[#056839]">
                          {c.received_weight_kg} kg
                        </span>
                      </div>
                      {collectionMaterials(c).length > 0 && (
                        <div
                          className="text-[11px] text-[#3F3F3F]/80 mt-0.5 font-mono"
                          data-testid={`input-materials-${c.id}`}
                        >
                          {collectionMaterials(c)
                            .map((m) => `${m.material_type}: ${m.weight_kg.toFixed(1)} kg`)
                            .join(" · ")}
                        </div>
                      )}
                      <div className="text-[11px] text-[#3F3F3F]/70 truncate">
                        {c.source_location} · {formatDate(c.timestamp)}
                      </div>
                    </div>
                  </label>
                ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Step 2: processing types (multi) */}
        <Card className="border-[#D2E4D6]/80 rounded-xl">
          <CardContent className="p-5 md:p-6 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-semibold">2 · Processing types</h2>
              <span className="text-xs text-[#3F3F3F]/60">Select one or more</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="processing-type-grid">
              {PROCESSING_TYPES.map((p) => {
                const active = processingTypes.includes(p);
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => toggleType(p)}
                    data-testid={`processing-type-${p.toLowerCase()}`}
                    className={`h-12 rounded-lg border-2 font-medium text-sm transition-all ${
                      active
                        ? "border-[#056839] bg-[#D2E4D6] text-[#056839]"
                        : "border-[#E4D5B7] bg-white text-[#3F3F3F] hover:border-[#69A488]"
                    }`}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Selected-inputs breakdown — gives the user a per-material picture
             of what's in the batch they've assembled, so they know how much of
             each material they can plausibly declare as an output below. */}
        {selectedByMaterial.length > 0 && (
          <Card
            className="border-[#056839]/25 bg-[#D2E4D6]/30 rounded-xl"
            data-testid="selected-materials-summary"
          >
            <CardContent className="p-4 md:p-5">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#056839] font-semibold">
                    In your selected batch
                  </div>
                  <div className="text-xs text-[#3F3F3F]/80 mt-0.5">
                    Use these totals to size your output batches below.
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedByMaterial.map((m) => (
                    <div
                      key={m.material_type}
                      data-testid={`selected-material-${m.material_type}`}
                      className="flex items-baseline gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#056839]/30"
                    >
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-[#3F3F3F]">
                        {m.material_type}
                      </span>
                      <span className="font-mono text-sm font-semibold text-[#056839]">
                        {m.weight_kg.toFixed(1)} kg
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: outputs (intermediate batches) */}
        <Card className="border-[#D2E4D6]/80 rounded-xl">
          <CardContent className="p-5 md:p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-semibold">3 · Output batches</h2>
              <Button
                size="sm"
                variant="outline"
                onClick={addOutput}
                data-testid="add-output-btn"
                className="border-[#056839] text-[#056839] hover:bg-[#D2E4D6]"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add batch
              </Button>
            </div>
            <div className="space-y-3" data-testid="outputs-list">
              {outputs.map((o, idx) => (
                <div
                  key={o._uid || `output-${idx}`}
                  className="grid grid-cols-1 md:grid-cols-12 gap-2 p-3 bg-[#F9F8F6] rounded-lg"
                >
                  <div className="md:col-span-4">
                    <Label className="text-[10px] uppercase tracking-wider text-[#69A488]">
                      Material
                    </Label>
                    <Select
                      value={o.material_type}
                      onValueChange={(v) => setOutput(idx, "material_type", v)}
                    >
                      <SelectTrigger
                        className="h-10 mt-1"
                        data-testid={`output-material-${idx}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MATERIALS.map((m) => (
                          <SelectItem key={m} value={m}>
                            {m}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-4">
                    <Label className="text-[10px] uppercase tracking-wider text-[#69A488]">Form</Label>
                    <Select value={o.form} onValueChange={(v) => setOutput(idx, "form", v)}>
                      <SelectTrigger className="h-10 mt-1" data-testid={`output-form-${idx}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FORMS.map((m) => (
                          <SelectItem key={m} value={m}>
                            {m}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-3">
                    <Label className="text-[10px] uppercase tracking-wider text-[#69A488]">
                      Weight (kg)
                    </Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      value={o.weight_kg}
                      onChange={(e) => setOutput(idx, "weight_kg", e.target.value)}
                      data-testid={`output-weight-${idx}`}
                      className="h-10 mt-1"
                    />
                  </div>
                  <div className="md:col-span-1 flex items-end">
                    {outputs.length > 1 && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => removeOutput(idx)}
                        data-testid={`remove-output-${idx}`}
                        className="text-[#A0522D] h-10 w-full md:w-10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-[#D2E4D6]">
              <div className="text-sm text-[#3F3F3F]">
                Output: <span className="font-mono font-semibold">{totalOutput.toFixed(2)} kg</span>
              </div>
              <div className="text-sm text-[#3F3F3F]">
                Residual:{" "}
                <span
                  className={`font-mono font-semibold ${
                    overflow ? "text-[#A0522D]" : "text-[#056839]"
                  }`}
                  data-testid="residual"
                >
                  {residual.toFixed(2)} kg
                </span>
              </div>
            </div>
            {overflow && (
              <div className="text-sm bg-[#A0522D]/10 text-[#A0522D] px-3 py-2 rounded-lg">
                Outputs exceed total input. Reduce weights to continue.
              </div>
            )}
            {!overflow && materialOverflow.length > 0 && (
              <div
                className="text-sm bg-[#A0522D]/10 text-[#A0522D] px-3 py-2 rounded-lg space-y-1"
                data-testid="material-overflow-banner"
              >
                <div className="font-medium">Per-material mass balance failed:</div>
                <ul className="list-disc pl-5 text-xs">
                  {materialOverflow.map((m) => (
                    <li key={m.material_type}>
                      <span className="font-semibold">{m.material_type}</span>: outputs{" "}
                      <span className="font-mono">{m.out_kg.toFixed(2)} kg</span> exceed input{" "}
                      <span className="font-mono">{m.in_kg.toFixed(2)} kg</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex flex-wrap justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => navigate("/facility")}
            data-testid="cancel-processing-btn"
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={saveDraft}
            disabled={savingDraft || busy}
            data-testid="save-draft-btn"
            className="border-[#A0522D] text-[#A0522D] hover:bg-[#A0522D]/10 h-11 px-5"
          >
            {savingDraft ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <><Save className="w-4 h-4 mr-2" /> Save draft</>
            )}
          </Button>
          <Button
            onClick={submit}
            disabled={busy || overflow || materialOverflow.length > 0 || savingDraft}
            data-testid="submit-processing-btn"
            className="gfs-bg-primary hover:bg-[#044E2A] text-white h-11 px-6"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Cog className="w-4 h-4 mr-2" />}
            Submit processing
          </Button>
        </div>
      </div>
    </div>
  );
}
